import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, BellRing, Save, Plus, Pencil, Check, X, ListChecks, UserPlus, Activity, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWatchlists } from "@/hooks/useWatchlists";
import { StatusBadge, LeagueChip, CATEGORY_META } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";

const CERTAINTY = [
  { v: "reported", label: "All (incl. reported)" },
  { v: "questionable", label: "Questionable & up" },
  { v: "official", label: "Official only" },
];

function ListCard({ list, limits, onRename, onDelete, onRemovePlayer, canDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(list.name);

  const save = async () => {
    if (name.trim() && name.trim() !== list.name) await onRename(list.id, name.trim());
    setEditing(false);
  };

  return (
    <div data-testid={`watchlist-card-${list.id}`} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        {editing ? (
          <div className="flex items-center gap-1.5 flex-1">
            <input data-testid={`list-rename-input-${list.id}`} autoFocus value={name} maxLength={40}
              onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
              className="flex-1 bg-[#151B2B] border border-[#232D42] rounded-lg px-2.5 py-1 text-sm text-slate-100" />
            <button data-testid={`list-rename-save-${list.id}`} onClick={save} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-[#00E5A3]"><Check size={15} /></button>
            <button onClick={() => { setEditing(false); setName(list.name); }} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-500"><X size={15} /></button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <ListChecks size={16} className="text-[#00E5A3] shrink-0" />
              <h3 className="font-heading font-semibold text-slate-100 truncate">{list.name}</h3>
              <span className="text-xs font-mono text-slate-500 shrink-0">{list.players.length}/{limits.per_list}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button data-testid={`list-edit-${list.id}`} onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-500 hover:text-slate-200"><Pencil size={14} /></button>
              {canDelete && (
                <button data-testid={`list-delete-${list.id}`} onClick={() => onDelete(list.id)} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-500 hover:text-[#FF4D4D]"><Trash2 size={14} /></button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        {list.players.length === 0 && (
          <div className="text-center py-6 border border-dashed border-[#232D42] rounded-lg">
            <p className="text-slate-500 text-xs">No players yet.</p>
            <Link to="/feed" className="text-[#00E5A3] text-xs hover:underline">Find players in the feed →</Link>
          </div>
        )}
        {list.players.map((p) => (
          <div key={p.id} data-testid={`watchlist-item-${p.id}`} className="bg-[#151B2B] border border-[#232D42] rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1"><LeagueChip league={p.league} /><StatusBadge status={p.status} /></div>
              <Link to={`/players/${p.id}`} className="font-heading font-semibold text-slate-100 hover:text-[#00E5A3] text-sm">{p.name}</Link>
              <span className="text-xs text-slate-500 font-mono ml-2">{p.team_abbr} · {p.position}</span>
              {p.status_note && <p className="text-xs text-[#FFB020] mt-1">{p.status_note}</p>}
            </div>
            <button data-testid={`watchlist-remove-${p.id}`} onClick={() => onRemovePlayer(list.id, p.id)}
              className="p-2 rounded-lg hover:bg-[#1C2438] text-slate-500 hover:text-[#FF4D4D] shrink-0">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Watchlist() {
  const { user, refresh } = useAuth();
  const { lists, limits, isGuest, createList, renameList, deleteList, removePlayer } = useWatchlists();
  const [prefs, setPrefs] = useState(user?.alert_prefs || { categories: [], min_certainty: "reported", channels: ["in_app"], goalie_alerts: true, team_injury_alerts: true, daily_digest: false, weekly_recap: false });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [feed, setFeed] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [soundOn, setSoundOn] = useState(localStorage.getItem("sq_alert_sound") !== "off");
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("sq_alert_sound", next ? "on" : "off");
    toast.success(next ? "Alert sound on" : "Alert sound off");
  };
  const sendDigestPreview = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/alerts/digest-test");
      if (r.data?.ok) toast.success(`Digest sent to ${r.data.sent_to} — ${r.data.count} update(s).`);
      else toast.error(r.data?.error ? `Send failed: ${r.data.error}` : (r.data?.reason || "Couldn't send digest."), { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally { setSendingDigest(false); }
  };
  const sendRecapPreview = async () => {
    setSendingRecap(true);
    try {
      const r = await api.post("/alerts/recap-test");
      if (r.data?.ok) toast.success(`Weekly recap sent to ${r.data.sent_to}.`);
      else toast.error(r.data?.error ? `Send failed: ${r.data.error}` : (r.data?.reason || "Couldn't send recap."), { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send recap");
    } finally { setSendingRecap(false); }
  };

  useEffect(() => { if (user?.alert_prefs) setPrefs(user.alert_prefs); }, [user]);
  useEffect(() => {
    if (!user) { setFeed(null); return; }
    api.get("/watchlist/injury-feed").then((r) => setFeed(r.data)).catch(() => setFeed({ events: [] }));
  }, [user]);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/alerts/test-email");
      if (r.data?.ok) toast.success(`Test email sent to ${r.data.sent_to}. Check your inbox (and spam).`);
      else toast.error(r.data?.error ? `Send failed: ${r.data.error}` : (r.data?.reason || "Email isn't configured yet."), { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test email");
    } finally { setSendingTest(false); }
  };

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await createList(newName.trim());
      setNewName(""); setCreating(false);
      toast.success("Watchlist created");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not create list");
    }
  };

  const onDelete = async (id) => { await deleteList(id); toast.success("Watchlist deleted"); };
  const onRename = async (id, name) => { await renameList(id, name); toast.success("Renamed"); };
  const onRemovePlayer = async (listId, pid) => { await removePlayer(listId, pid); };

  const toggleCat = (cat) => {
    const set = new Set(prefs.categories);
    set.has(cat) ? set.delete(cat) : set.add(cat);
    setPrefs({ ...prefs, categories: [...set] });
  };
  const emailOn = (prefs.channels || ["in_app"]).includes("email");
  const toggleEmail = () => {
    const set = new Set(prefs.channels || ["in_app"]);
    set.has("email") ? set.delete("email") : set.add("email");
    set.add("in_app");
    setPrefs({ ...prefs, channels: [...set] });
  };
  const savePrefs = async () => {
    await api.put("/me/alert-prefs", prefs);
    await refresh();
    toast.success("Alert preferences saved");
  };

  const atListCap = lists.length >= limits.lists;

  return (
    <div className="max-w-[1200px] mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Watchlists & Alerts</h1>
        <p className="text-sm text-slate-400 mt-1">Organize players into named lists and tune which status changes alert you.</p>
        {isGuest && (
          <p data-testid="guest-watchlist-note" className="text-xs text-[#38BDF8] mt-2 font-mono">
            Guest mode · your lists are saved privately on this device. <Link to="/register" className="underline">Create a free account</Link> and they transfer automatically — plus alerts.
          </p>
        )}
        {user?.tier === "free" && (
          <p className="text-xs text-[#FFB020] mt-2 font-mono">Rookie: 1 list · 3 players. <Link to="/pro" className="underline">Upgrade</Link> for more named lists.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{lists.length} of {limits.lists} list{limits.lists > 1 ? "s" : ""}</span>
            {creating ? (
              <div className="flex items-center gap-1.5">
                <input data-testid="new-list-input" autoFocus value={newName} maxLength={40}
                  onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
                  placeholder="List name…" className="bg-[#151B2B] border border-[#232D42] rounded-lg px-2.5 py-1.5 text-sm text-slate-100" />
                <Button data-testid="new-list-create-btn" onClick={create} className="h-8 px-3 bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] text-xs font-semibold">Create</Button>
                <button onClick={() => { setCreating(false); setNewName(""); }} className="p-1.5 text-slate-500 hover:text-slate-200"><X size={16} /></button>
              </div>
            ) : (
              <Button data-testid="new-list-btn"
                onClick={() => atListCap
                  ? toast.error(isGuest ? "Guest mode includes 1 list — create a free account for more." : `Your plan includes ${limits.lists} watchlist(s). Upgrade for more.`)
                  : setCreating(true)}
                className="h-8 px-3 bg-[#151B2B] border border-[#232D42] hover:bg-[#1C2438] text-slate-200 text-xs font-semibold">
                <Plus size={14} className="mr-1" /> New list
              </Button>
            )}
          </div>

          <div className="space-y-4" data-testid="watchlists-container">
            {lists.map((l) => (
              <ListCard key={l.id} list={l} limits={limits} onRename={onRename} onDelete={onDelete}
                onRemovePlayer={onRemovePlayer} canDelete={lists.length > 1} />
            ))}
          </div>

          {user && feed && (feed.events?.length > 0) && (
            <div className="mt-6" data-testid="watchlist-injury-feed">
              <div className="flex items-center gap-2 mb-3">
                <Activity size={16} className="text-[#FFB020]" />
                <h3 className="font-heading font-semibold text-slate-100">Injury feed</h3>
                <span className="text-[11px] font-mono text-slate-500">across your {feed.watched_players} player{feed.watched_players !== 1 ? "s" : ""} &amp; {feed.watched_teams} team{feed.watched_teams !== 1 ? "s" : ""}</span>
              </div>
              <div className="rounded-xl bg-[#0B0F18] border border-[#232D42] divide-y divide-[#1A2133]">
                {feed.events.map((e) => (
                  <div key={e.id} data-testid={`feed-event-${e.id}`} className="flex items-start gap-3 px-4 py-3">
                    <span className={`mt-1.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0 ${e.scope === "team" ? "text-[#38BDF8] bg-[#38BDF8]/10" : "text-[#00E5A3] bg-[#00E5A3]/10"}`}>
                      {e.scope}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Link to={`/players/${e.player_id}`} className="text-sm font-semibold text-slate-100 hover:text-[#00E5A3] truncate">{e.headline}</Link>
                        <span className="ml-auto text-[10px] font-mono text-slate-500 shrink-0">{relativeTime(e.timestamp_utc)}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">{e.league} · {e.team}{e.severity ? ` · ${e.severity}` : ""}</p>
                      {e.source_url && (
                        <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono text-[#38BDF8] hover:underline mt-1 inline-block">{e.source || "Source"} ↗</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Alert prefs (authed) or sign-up nudge (guest) */}
        <div className="lg:col-span-5">
          {user ? (
            <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4"><BellRing size={17} className="text-[#00E5A3]" /><h3 className="font-heading font-semibold text-slate-100">Alert Preferences</h3></div>

              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Categories to alert on</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(CATEGORY_META).map(([k, m]) => {
                  const active = prefs.categories.includes(k);
                  return (
                    <button key={k} data-testid={`pref-cat-${k}`} onClick={() => toggleCat(k)}
                      className="text-xs font-mono px-2.5 py-1 rounded-lg border transition-colors"
                      style={active ? { color: m.color, background: `${m.color}18`, borderColor: `${m.color}55` } : { color: "#94A3B8", borderColor: "#232D42" }}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-600 mb-5 font-mono">Leave all unselected to receive alerts for every category.</p>

              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Minimum certainty</p>
              <select data-testid="pref-certainty" value={prefs.min_certainty} onChange={(e) => setPrefs({ ...prefs, min_certainty: e.target.value })}
                className="w-full bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200 font-mono mb-5">
                {CERTAINTY.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>

              <div className="flex items-center justify-between mb-5 pt-1">
                <div>
                  <p className="text-sm text-slate-200">Starting goalie alerts</p>
                  <p className="text-[11px] text-slate-500">NHL — ping me when a starter is confirmed or changes</p>
                </div>
                <button data-testid="pref-goalie-toggle" role="switch" aria-checked={prefs.goalie_alerts ?? true}
                  onClick={() => setPrefs({ ...prefs, goalie_alerts: !(prefs.goalie_alerts ?? true) })}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${(prefs.goalie_alerts ?? true) ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${(prefs.goalie_alerts ?? true) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-5 pt-1">
                <div>
                  <p className="text-sm text-slate-200">Alert sound</p>
                  <p className="text-[11px] text-slate-500">Play a subtle chime when a new alert arrives</p>
                </div>
                <button data-testid="pref-sound-toggle" role="switch" aria-checked={soundOn}
                  onClick={toggleSound}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${soundOn ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${soundOn ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-5 pt-1">
                <div>
                  <p className="text-sm text-slate-200">Team injury alerts</p>
                  <p className="text-[11px] text-slate-500">Alert me when any player on a team I follow hits the injury report</p>
                </div>
                <button data-testid="pref-team-toggle" role="switch" aria-checked={prefs.team_injury_alerts ?? true}
                  onClick={() => setPrefs({ ...prefs, team_injury_alerts: !(prefs.team_injury_alerts ?? true) })}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${(prefs.team_injury_alerts ?? true) ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${(prefs.team_injury_alerts ?? true) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-5 pt-1">
                <div>
                  <p className="text-sm text-slate-200">Practice status alerts</p>
                  <p className="text-[11px] text-slate-500">NFL — ping me when a watched player's practice status changes (Full → Limited → DNP)</p>
                </div>
                <button data-testid="pref-practice-toggle" role="switch" aria-checked={prefs.practice_alerts ?? true}
                  onClick={() => setPrefs({ ...prefs, practice_alerts: !(prefs.practice_alerts ?? true) })}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${(prefs.practice_alerts ?? true) ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${(prefs.practice_alerts ?? true) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-5 pt-1">
                <div>
                  <p className="text-sm text-slate-200">Email me alerts</p>
                  <p className="text-[11px] text-slate-500">
                    {user?.email ? <>Send to <span className="text-slate-400">{user.email}</span></> : "Also email me when a watched player changes status"}
                  </p>
                </div>
                <button data-testid="pref-email-toggle" role="switch" aria-checked={emailOn}
                  onClick={toggleEmail}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${emailOn ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${emailOn ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              {emailOn && (
                <button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={sendingTest}
                  className="w-full mb-5 flex items-center justify-center gap-1.5 text-xs font-mono text-[#38BDF8] border border-[#38BDF8]/30 rounded-lg py-2 hover:bg-[#38BDF8]/10 transition-colors disabled:opacity-50">
                  <Mail size={13} /> {sendingTest ? "Sending…" : "Send me a test email"}
                </button>
              )}

              <div className="flex items-center justify-between mb-4 pt-1 border-t border-[#232D42]/60">
                <div className="pt-4">
                  <p className="text-sm text-slate-200">Daily email digest</p>
                  <p className="text-[11px] text-slate-500">One email a day summarizing every status change on your players &amp; teams</p>
                </div>
                <button data-testid="pref-digest-toggle" role="switch" aria-checked={prefs.daily_digest ?? false}
                  onClick={() => setPrefs({ ...prefs, daily_digest: !(prefs.daily_digest ?? false) })}
                  className={`relative shrink-0 mt-4 w-11 h-6 rounded-full transition-colors ${(prefs.daily_digest ?? false) ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${(prefs.daily_digest ?? false) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {(prefs.daily_digest ?? false) && (
                <div className="mb-5 rounded-lg bg-[#0B0F18] border border-[#232D42] p-3" data-testid="digest-timing">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Send my digest at</p>
                  <div className="flex items-center gap-2 mb-3">
                    <select data-testid="digest-hour-select" value={prefs.digest_hour ?? 8}
                      onChange={(e) => setPrefs({ ...prefs, digest_hour: parseInt(e.target.value, 10) })}
                      className="flex-1 bg-[#151B2B] border border-[#232D42] rounded-lg px-2.5 py-2 text-sm text-slate-200 font-mono">
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{h === 0 ? "12:00 AM" : h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`}</option>
                      ))}
                    </select>
                    <select data-testid="digest-tz-select" value={prefs.digest_tz ?? "America/New_York"}
                      onChange={(e) => setPrefs({ ...prefs, digest_tz: e.target.value })}
                      className="flex-1 bg-[#151B2B] border border-[#232D42] rounded-lg px-2.5 py-2 text-sm text-slate-200 font-mono">
                      {[
                        ["America/New_York", "Eastern"],
                        ["America/Chicago", "Central"],
                        ["America/Denver", "Mountain"],
                        ["America/Los_Angeles", "Pacific"],
                        ["America/Phoenix", "Arizona"],
                        ["America/Anchorage", "Alaska"],
                        ["Pacific/Honolulu", "Hawaii"],
                        ["Europe/London", "London"],
                        ["UTC", "UTC"],
                      ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <button data-testid="send-digest-preview-btn" onClick={sendDigestPreview} disabled={sendingDigest}
                    className="w-full flex items-center justify-center gap-1.5 text-xs font-mono text-[#38BDF8] border border-[#38BDF8]/30 rounded-lg py-2 hover:bg-[#38BDF8]/10 transition-colors disabled:opacity-50">
                    <Mail size={13} /> {sendingDigest ? "Sending…" : "Send me a digest preview now"}
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between mb-4 pt-1 border-t border-[#232D42]/60">
                <div className="pt-4">
                  <p className="text-sm text-slate-200">Weekly recap email</p>
                  <p className="text-[11px] text-slate-500">A Monday-morning rundown of the week's biggest status swings, written like a veteran broadcaster</p>
                </div>
                <button data-testid="pref-recap-toggle" role="switch" aria-checked={prefs.weekly_recap ?? false}
                  onClick={() => setPrefs({ ...prefs, weekly_recap: !(prefs.weekly_recap ?? false) })}
                  className={`relative shrink-0 mt-4 w-11 h-6 rounded-full transition-colors ${(prefs.weekly_recap ?? false) ? "bg-[#00E5A3]" : "bg-[#232D42]"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${(prefs.weekly_recap ?? false) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {(prefs.weekly_recap ?? false) && (
                <button data-testid="send-recap-preview-btn" onClick={sendRecapPreview} disabled={sendingRecap}
                  className="w-full mb-5 flex items-center justify-center gap-1.5 text-xs font-mono text-[#38BDF8] border border-[#38BDF8]/30 rounded-lg py-2 hover:bg-[#38BDF8]/10 transition-colors disabled:opacity-50">
                  <Mail size={13} /> {sendingRecap ? "Writing your recap…" : "Send me a recap preview now"}
                </button>
              )}

              <Button data-testid="save-prefs-btn" onClick={savePrefs} className="w-full bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-10">
                <Save size={15} className="mr-1.5" /> Save Preferences
              </Button>
            </div>
          ) : (
            <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-6 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#00E5A3]/10 flex items-center justify-center mx-auto mb-4"><BellRing size={22} className="text-[#00E5A3]" /></div>
              <h3 className="font-heading font-semibold text-slate-100">Turn on alerts</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">Create a free account to get alerts the moment a player you follow changes status. Your current device lists come with you automatically.</p>
              <Button asChild className="w-full mt-5 bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-10">
                <Link to="/register" data-testid="guest-signup-btn"><UserPlus size={15} className="mr-1.5" /> Create Free Account</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
